import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface HeroThreadPart {
	type: "normal" | "italic";
	text: string;
}

interface HeroHeaderProps {
	userName: string;
	threadParts?: HeroThreadPart[];
}

export function HeroHeader({ userName, threadParts }: HeroHeaderProps) {
	const getGreeting = (): string => {
		const hour = new Date().getHours();
		if (hour >= 5 && hour < 12) return "Good morning";
		if (hour >= 12 && hour < 17) return "Good afternoon";
		if (hour >= 17 && hour < 24) return "Good evening";
		return "Good night";
	};

	const renderThreadMessage = () => {
		if (!threadParts || threadParts.length === 0) {
			return (
				<span className="italic font-display text-dashboard-muted-dark">
					You're up to date.
				</span>
			);
		}

		return (
			<>
				{threadParts.map((part) =>
					part.type === "italic" ? (
						<span
							key={part.text}
							className="font-display italic font-semibold text-dashboard-muted-dark"
						>
							{part.text}
						</span>
					) : (
						<span key={part.text}>{part.text}</span>
					),
				)}
			</>
		);
	};

	return (
		<div className="dashboard-container" style={{ padding: "72px 0 56px" }}>
			<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
				VOL. III · ISSUE No. 142 · CHAPTER ONE
			</p>

			<h1 className="mt-5 font-display text-[56px] font-medium leading-none tracking-tighter text-dashboard-text">
				{getGreeting()},
				<br />
				<span className="block font-display text-[120px] italic font-medium leading-[0.85] tracking-tighter text-dashboard-accent-display">
					{userName}.
				</span>
			</h1>

			<div className="mt-9 grid grid-cols-[120px_1fr] gap-8">
				<div className="mt-4 h-px bg-dashboard-warm-300" />

				<p className="max-w-xl font-body text-[18px] leading-[1.75] text-dashboard-text">
					{renderThreadMessage()}
				</p>
			</div>
		</div>
	);
}

/**
 * Words the public-projects search matches on a project's title, city,
 * designer or notes. They are prompts, not a claim about anyone's history: the
 * SearchBar labels them TRY.
 */
export const HOME_SEARCH_SUGGESTIONS = [
	"Kitchen",
	"Bedroom",
	"Living room",
	"Bathroom",
] as const;

interface SearchBarProps {
	value: string;
	onChange: (value: string) => void;
	/**
	 * Real search history when a caller has one. Nothing records searches
	 * today, so the dashboard omits it and the RECENT row stays hidden
	 * (issue #378 removed the invented history that used to sit here).
	 */
	recentSearches?: readonly string[];
	/** Example queries rendered as TRY chips. */
	suggestions?: readonly string[];
	placeholder?: string;
	onSuggestionClick?: (query: string) => void;
	/** Called with the current query when the user presses Enter or Search. */
	onSearch?: (query: string) => void;
	noContainer?: boolean;
	hideSuggestions?: boolean;
}

export function SearchBar({
	value,
	onChange,
	recentSearches,
	suggestions,
	placeholder,
	onSuggestionClick,
	onSearch,
	noContainer = false,
	hideSuggestions = false,
}: SearchBarProps) {
	const { t } = useTranslation();
	const [isHovered, setIsHovered] = useState(false);

	const handleSubmit = () => {
		if (value.trim()) {
			onSearch?.(value.trim());
		}
	};

	const searchBarContent = (
		<>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: mouse handlers only toggle decorative hover styling, no interactive behavior */}
			<div
				className={cn(
					"flex items-center gap-4 rounded-2xl border border-dashboard-border px-5 py-4 backdrop-blur-md transition-all duration-300 dashboard-search-bar",
					isHovered && "dashboard-search-bar-elevated",
				)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				{/* Animated Orb */}
				<div
					className="h-9 w-9 shrink-0 overflow-hidden rounded-full"
					style={{
						background:
							"radial-gradient(circle at 30% 30%, rgb(255, 237, 213) 0%, rgb(249, 115, 22) 50%, rgb(234, 88, 12) 100%)",
						boxShadow:
							"color-mix(in srgb, var(--color-dashboard-paper) 50%, transparent) 0px 1px 1px inset, color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent) 0px -2px 4px inset, color-mix(in srgb, var(--color-dashboard-accent) 45%, transparent) 0px 4px 12px -2px, color-mix(in srgb, var(--color-dashboard-accent) 18%, transparent) 0px 0px 0px 1px",
					}}
				>
					<div
						className="h-full w-full rounded-full"
						style={{
							background:
								"conic-gradient(transparent 0%, rgba(255, 237, 213, 0.6) 25%, transparent 50%, rgba(254, 215, 170, 0.4) 75%, transparent 100%)",
							animation: "orbSpin 6000ms linear infinite",
						}}
					/>
				</div>

				<div className="flex-1">
					<input
						type="text"
						placeholder={placeholder ?? t("homeSearch.placeholder")}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.nativeEvent.isComposing) {
								handleSubmit();
							}
						}}
						className="w-full border-none bg-none font-display italic text-xl font-normal text-dashboard-text outline-none placeholder:text-dashboard-placeholder-light"
						style={{ letterSpacing: "-0.01em", cursor: "text" }}
						aria-label="Search"
					/>
					{recentSearches && recentSearches.length > 0 && (
						<div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1">
							<span className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
								RECENT
							</span>
							{recentSearches.map((query) => (
								<button
									key={query}
									type="button"
									className="bg-none border-none p-0 font-body text-xs italic text-dashboard-muted cursor-pointer transition-colors duration-200 hover:text-dashboard-accent-dark"
									onClick={() => onSuggestionClick?.(query)}
								>
									"{query}"
								</button>
							))}
						</div>
					)}
				</div>

				<div className="h-9 w-px bg-dashboard-border-soft" />

				<button
					type="button"
					className="dashboard-primary-button flex h-11 cursor-pointer items-center gap-2 rounded-xl border-none px-5 font-body text-sm font-semibold text-dashboard-on-fill transition-all duration-200"
					onClick={handleSubmit}
				>
					Search
					<ArrowRight className="h-4 w-4" />
				</button>
			</div>

			{!hideSuggestions && suggestions && suggestions.length > 0 && (
				<div className="mt-3 flex gap-2 pl-3 flex-wrap">
					<span className="self-center font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted mr-1">
						TRY
					</span>
					{suggestions.map((query) => (
						<button
							key={query}
							type="button"
							className="rounded-full border border-dashboard-border bg-none px-3 py-1 font-body text-xs text-dashboard-muted cursor-pointer transition-all duration-200 hover:border-dashboard-accent hover:text-dashboard-accent-dark"
							onClick={() => onSuggestionClick?.(query)}
						>
							{query}
						</button>
					))}
				</div>
			)}
		</>
	);

	if (noContainer) {
		return <div className="w-full">{searchBarContent}</div>;
	}

	return (
		<div className="dashboard-container" style={{ paddingBottom: "24px" }}>
			{searchBarContent}
		</div>
	);
}
